import * as React from 'react';
import { Tooltip, Typography, TypographyProps } from '@mui/material';

export type EllipsisCellProps = {
    /** Texte affiché, utilisé aussi pour la tooltip et la mesure de débordement. */
    text?: string | null;
    color?: TypographyProps['color'];
};

/**
 * Cellule de tableau adaptative : le texte s'affiche en entier s'il tient dans la
 * largeur de la colonne, et n'est tronqué (fondu sur la droite + tooltip) que
 * lorsqu'il déborde réellement — détection via `scrollWidth > clientWidth`,
 * re-mesurée au redimensionnement de la colonne (ResizeObserver).
 */
const EllipsisCell: React.FunctionComponent<EllipsisCellProps> = function ({ text, color }) {
    const ref = React.useRef<HTMLSpanElement>(null);
    const [isOverflowing, setIsOverflowing] = React.useState(false);
    const value = text ?? '';

    React.useLayoutEffect(() => {
        const element = ref.current;
        if (!element) {
            return undefined;
        }

        const measure = () => {
            setIsOverflowing(element.scrollWidth > element.clientWidth);
        };

        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(element);

        return () => observer.disconnect();
    }, [value]);

    return (
        <Tooltip
            title={isOverflowing ? value : ''}
            placement="top"
        >
            <Typography
                ref={ref}
                component="span"
                variant="l2dtableCell"
                color={color ?? 'text.gris70'}
                sx={{
                    display: 'block',
                    width: '100%',
                    // Permet de rétrécir sous la largeur du contenu dans la cellule flex
                    // de la datatable (sinon le texte ne se tronque jamais).
                    minWidth: 0,
                    flexGrow: 1,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    ...(isOverflowing && {
                        maskImage: 'linear-gradient(to right, #000 85%, transparent)',
                        WebkitMaskImage: 'linear-gradient(to right, #000 85%, transparent)',
                    }),
                }}
            >
                {value}
            </Typography>
        </Tooltip>
    );
};

export default EllipsisCell;
